import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react'

import { IconBaseProps } from 'react-icons'
import { FiAlertCircle } from 'react-icons/fi'

import { useFormikContext, useField } from 'formik'

import Tooltip from '../Tooltip'

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string
  placeholder: string
  icon?: React.ComponentType<IconBaseProps>
  mask?: 'cep' | 'currency' | 'cpf' | 'phone' | 'date' | 'rg'
  sizeBox?: 'small' | 'large'
  prefix?: string
  sufix?: string
}

const Input: React.FC<Props> = ({
  prefix,
  sufix,
  placeholder,
  name,
  icon: Icon,
  mask,
  sizeBox = 'small',
  type,
  value: valueProp,
  onChange: onChangeProp,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
  defaultValue,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const [isFocused, setIsFocused] = useState(false)
  const [isFilled, setIsFilled] = useState(false)

  let isFormik = true
  try {
    useFormikContext()
  } catch {
    isFormik = false
  }

  const formikField = useField(name)

  const field = isFormik ? formikField[0] : null
  const meta = isFormik ? formikField[1] : null
  const helpers = isFormik ? formikField[2] : null

  const error = meta?.touched ? meta?.error : undefined

  useEffect(() => {
    if (type === 'date' && !!inputRef.current?.value) {
      inputRef.current.type = type
    }
  }, [type])

  const handleInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)

      if (type === 'date' && inputRef.current) {
        inputRef.current.type = 'date'
      }

      onFocusProp?.(e)
    },
    [onFocusProp, type],
  )

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      setIsFilled(!!inputRef.current?.value)

      if (type === 'date' && inputRef.current?.value === '') {
        inputRef.current.type = 'text'
      }

      if (isFormik) {
        field?.onBlur(e)
        if (inputRef.current?.value) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          helpers?.setError(undefined as any)
        }
      }

      onBlurProp?.(e)
    },
    [field, helpers, isFormik, onBlurProp, type],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let nextValue = e.target.value

      if (mask === 'cpf') {
        nextValue = nextValue
          .replace(/\D/g, '')
          .slice(0, 11)
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      } else if (mask === 'cep') {
        nextValue = nextValue
          .replace(/\D/g, '')
          .slice(0, 8)
          .replace(/^(\d{5})(\d)/, '$1-$2')
      } else if (mask === 'phone') {
        nextValue = nextValue
          .replace(/\D/g, '')
          .slice(0, 11)
          .replace(/^(\d{2})(\d)/g, '($1) $2')
          .replace(/(\d)(\d{4})$/, '$1-$2')
      } else if (mask === 'date') {
        nextValue = nextValue
          .replace(/\D/g, '')
          .slice(0, 8)
          .replace(/(\d{2})(\d)/, '$1/$2')
          .replace(/(\d{2})(\d)/, '$1/$2')
      } else if (mask === 'rg') {
        nextValue = nextValue
          .toUpperCase()
          .replace(/[^0-9X]/g, '')
          .slice(0, 9)
          .replace(/^(\d{2})(\d)/, '$1.$2')
          .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/^(\d{2})\.(\d{3})\.(\d{3})([0-9X])$/, '$1.$2.$3-$4')
      }

      e.target.value = nextValue
      e.currentTarget.value = nextValue

      if (isFormik) {
        field?.onChange(e)
      } else {
        onChangeProp?.(e)
      }

      setIsFilled(!!nextValue)
    },
    [field, isFormik, mask, onChangeProp],
  )

  useEffect(() => {
    const v = isFormik ? field?.value : (valueProp ?? defaultValue ?? '')

    setIsFilled(!!v)
  }, [defaultValue, field?.value, isFormik, valueProp])

  const inputValue = isFormik ? (field?.value ?? '') : valueProp

  const isActive = isFocused || isFilled
  const hasSufix = !!sufix

  const borderColorClass = error
    ? 'border-[#C53030]'
    : isFocused
      ? 'border-[#8493F4]'
      : isFilled
        ? 'border-[rgba(132,147,244,0.6)]'
        : 'border-[rgba(174,174,174,0.6)]'

  const textColorClass = isFocused
    ? 'text-[#8493F4]'
    : isFilled
      ? 'text-[#D1D4E2]'
      : 'text-[#666360]'

  const paddingTopClass = isActive
    ? sizeBox === 'large'
      ? 'pt-[33px]'
      : 'pt-[24px]'
    : 'pt-[16px]'

  const containerClassName = [
    'relative flex w-full items-center rounded-[4px] border bg-transparent px-[12px] pb-[16px] mb-[12px] transition-[border-color,color,padding] duration-200',
    paddingTopClass,
    sizeBox === 'large' ? 'h-[70px]' : '',
    borderColorClass,
    textColorClass,
  ]
    .filter(Boolean)
    .join(' ')

  const labelClassName = [
    'absolute left-[12px] top-[16px] pr-[12px] text-[12px] font-bold text-[#666360] transition-all duration-200 pointer-events-none',
    isActive ? '-translate-y-[12px] tracking-[0.1em]' : '',
    error && sizeBox === 'large' ? 'text-[13px] pr-[30px]' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const iconClassName = [
    'mr-[12px]',
    isFilled ? 'text-[#8493F4]' : 'text-[#666360]',
  ].join(' ')

  const inputClassName = [
    'appearance-none border-0 bg-transparent text-[#333] outline-none placeholder:text-[#666360] [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]',
    hasSufix ? 'flex-0 text-right' : 'flex-1 text-left',
  ].join(' ')

  return (
    <div className={containerClassName}>
      {(isFilled || isFocused) && Icon && (
        <Icon size={20} className={iconClassName} />
      )}
      {(isFilled || isFocused) && prefix && (
        <span className="inline-block mr-[4px] text-[#333]">{prefix}</span>
      )}

      <input
        {...rest}
        ref={inputRef}
        name={name}
        type={type === 'date' ? 'text' : type}
        value={inputValue}
        defaultValue={isFormik ? undefined : defaultValue}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onChange={handleChange}
        className={inputClassName}
      />

      {(isFilled || isFocused) && sufix && (
        <span className="inline-block ml-[4px] text-[#333]">{sufix}</span>
      )}

      {error && (
        <Tooltip
          title={String(error)}
          className="absolute right-[10px] ml-[12px] h-[20px] [&>svg]:m-0"
        >
          <FiAlertCircle color="#c53030" size="20" />
        </Tooltip>
      )}

      <label htmlFor={name} className={labelClassName}>
        {placeholder}
      </label>
    </div>
  )
}

export default Input
